import React from 'react';
import { SSRConfig } from 'next-i18next';
import { GetServerSidePropsContext, NextPageContext, NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { defaultMetas } from '@constants/metas/default';
import extractFromContext from '@helpers/utils/extract-from-context';
import { usePreferences } from '@context/preferences.context';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import Head from '@components/atoms/head/head';
import Main from '@components/orders-sales/main';
import DefaultLayout from '@layouts/default-layout';
import { getAppInitialProps } from '@helpers/initial-props';

/*

***README***

FEATURE:
  - There are 5 main components: Order listing, Sale listing, Timeline, Timeline action(Popup) and Shipping Reminder
  - Shipping reminder belong to main.ts:
    + Data will be get from Sale listing API in 1 API call
    + Shipping reminder able to download Timeline - Execute a Timeline Action
  - Order lising and Sale listing belong to left.tsx:
    + Each of them have its own API
    + Both of them have search function, but we temporary hide it
    + Sale listing contains download report - A mini component
    + Nothing much about Order listing
  - Timeline belong to right.tsx:
    + Timeline can be downloaded by clicking on the Left Handside Item
    + Timeline will be automatically downloaded in first load if 1st Sale - 2nd Order has any item
    + Timeline will handle Timeline Action which will open a popin to show more information
    + Timeline will be reloaded after Action was executed to refetch data (This will be done by Poup by using fetch from React Query)
  - Timeline action (Popup) belong to popin.tsx:
    + It display form/information with buttons
    + Most likely use mutation form React Query to submit data with a click button
    + But only ConfirmSendInView will automatically submit because it was called from Shipping Reminder
  - Shipping reminder belong to main.tsx:
    + A mini component which display extra information from Sale API
    + It can trigger a download timline and Open a timeline action
    + Tricky: When set a timeline, we need to add the product to top of Sale list and scroll to top also! You will see it in the code.
  - Url query feature:
    + In Legacy my account page, user can click on some button/url and it will display Order / Sale / Timeline when webpage load
    + This ticket explain well: https://vestiairecollective.atlassian.net/browse/CS-2593
    + So, url query feature will override any other above logic

TECHNICAL:
- This page used 2 ways of state management system: React Query - Use State
- React query:
  + Used for working with API, InifiteQuery for listing, Query for timeline, Mutation for submit form
  + We cannot use SSR yet, because of https://app.slack.com/client/T02UNCY9H/D05BYPXJQN9?cdn_fallback=2
  + The benefit of React query is convinience on get/fetch/mutate API
  + WARNING with mutation: In rerendering of React, Mutation will be re-called, but no worry for Query because of caching
- Use state:
  + Used for components

*/

export type ServerSideProps = {
  props: SSRConfig;
};

const OrdersSalesPage: NextPageWithLayout = () => {
  const { language } = usePreferences();
  const pageMetas = defaultMetas[language];
  const { updateDataLayer } = useAnalyticEvents('orders_sales_timeline');
  React.useEffect(() => {
    updateDataLayer({
      screen_name: 'orders_sales',
      screen_category: 'my_account',
      screen_subcategory: 'my_account',
    });
  }, []); // Ignore this warning

  return (
    <PageAuthenticationGuard redirectionUrl={'/'}>
      <Head metadata={pageMetas} />
      <Main />
    </PageAuthenticationGuard>
  );
};

OrdersSalesPage.Layout = [DefaultLayout];
OrdersSalesPage.PageType = 'OrdersSalesPage';

export async function getServerSideProps(
  nextContext: NextPageContext | GetServerSidePropsContext,
): Promise<ServerSideProps> {
  const { locale } = extractFromContext(nextContext);
  const initialProps = await getAppInitialProps(nextContext);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'vc-authentication',
    'common',
    'orders-sales',
    'a11y',
  ]);

  return {
    props: {
      ...initialProps,
      ...translations,
    },
  };
}

export default OrdersSalesPage;
